import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { ExternalLink, Globe, Pencil } from 'lucide-react'
import { useTopBar } from '@/contexts/TopBarContext'
import { ownWebsiteApi, websiteError } from '@/api/website'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { EnquiriesCard } from './EnquiriesCard'

const STATUS_STYLE: Record<string, string> = {
  Live: 'bg-green-50 text-green-700 border-green-200',
  Draft: 'bg-amber-50 text-amber-700 border-amber-200',
  Offline: 'bg-gray-100 text-gray-600 border-gray-200',
  Suspended: 'bg-red-50 text-red-700 border-red-200',
}

/** The Website area of the admin app: where the site stands, a way into the editor, and the messages it has received. */
export default function WebsitePage() {
  useTopBar(<span className="text-sm font-medium text-gray-900">Website</span>)
  const qc = useQueryClient()
  const { data: status, isLoading } = useQuery({ queryKey: ['website-status'], queryFn: ownWebsiteApi.getStatus })
  const online = useMutation({
    mutationFn: (on: boolean) => ownWebsiteApi.setOnline(on),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['website-status'] }),
  })

  if (isLoading || !status) return <p className="text-gray-500">Loading…</p>

  if (!status.sitesEnabled || !status.hasSite) {
    return (
      <div className="max-w-2xl space-y-4">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><Globe size={22} className="text-indigo-500" /> Website</h1>
        <Card>
          <CardContent className="py-6 space-y-2">
            <p className="text-sm text-gray-700">
              {status.sitesEnabled
                ? "CertSlate is setting up your website. We'll let you know when it's ready to look at."
                : 'CertSlate Sites gives your training company its own website, built from your courses, with your logo and colours. Get in touch with CertSlate support to find out more.'}
            </p>
            {!status.sitesEnabled && <Link to="/admin/support" className="text-sm text-indigo-600 hover:underline">Contact support</Link>}
          </CardContent>
        </Card>
      </div>
    )
  }

  const live = status.status === 'Live'
  const address = status.primaryHostname ? `https://${status.primaryHostname}` : null

  return (
    <div className="max-w-4xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><Globe size={22} className="text-indigo-500" /> Website</h1>
        <p className="text-sm text-gray-500 mt-1">Your public website, built from your courses.</p>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Your website</CardTitle>
          {status.status && <span className={`text-xs font-medium rounded-full border px-2.5 py-1 ${STATUS_STYLE[status.status] ?? ''}`}>{status.status === 'Draft' ? 'Not published yet' : status.status}</span>}
        </CardHeader>
        <CardContent className="space-y-4">
          {address && (
            <p className="text-sm text-gray-700">
              Address:{' '}
              {live ? <a href={address} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline">{status.primaryHostname}</a> : <span className="font-medium">{status.primaryHostname}</span>}
            </p>
          )}
          {status.publishedAt && <p className="text-sm text-gray-500">Last published {new Date(status.publishedAt).toLocaleString('en-GB')}{status.hasUnpublishedChanges ? ' · you have unpublished changes' : ''}</p>}
          {status.status === 'Suspended' && <p className="text-sm text-red-700">This website has been suspended. Please contact CertSlate support.</p>}
          {!status.canEdit && status.status !== 'Suspended' && (
            <p className="text-sm text-gray-600">CertSlate is building your website for you. You'll be able to edit it yourself once it's ready.</p>
          )}
          {online.error && <p className="text-sm text-red-600">{websiteError(online.error).message}</p>}
          <div className="flex flex-wrap gap-2">
            {status.canEdit && status.status !== 'Suspended' && (
              <Button asChild><Link to="/admin/website/edit"><Pencil size={15} /> Edit website</Link></Button>
            )}
            {live && address && <Button variant="outline" asChild><a href={address} target="_blank" rel="noreferrer"><ExternalLink size={15} /> View live site</a></Button>}
            {status.canEdit && status.status === 'Live' && (
              <Button variant="ghost" disabled={online.isPending} onClick={() => { if (confirm('Take your website offline? Visitors will see a "not available" page until you put it back.')) online.mutate(false) }}>Take offline</Button>
            )}
            {status.canEdit && status.status === 'Offline' && <Button variant="outline" disabled={online.isPending} onClick={() => online.mutate(true)}>Put back online</Button>}
          </div>
        </CardContent>
      </Card>

      <EnquiriesCard api={ownWebsiteApi} />
    </div>
  )
}
